// @givmo/app-charity — the claim wizard's TERMINAL RESULT, held in the query cache (CPA-WEB51).
//
// ★ WHY THE CACHE. The wizard's success handler invalidates the memberships read (an approved claim
// installs the claimant as Owner, and the org switcher must learn it), and that refetch is what makes
// `PortalEntryGate` swap the onboarding chrome for the portal shell — `<OnboardingShell>` and
// `<AppShell>` are different component types at the same tree position, so the swap REMOUNTS the
// wizard. A result held in the old instance's `useState` dies with it: the fresh instance rendered
// the attestation form again under the full shell, and the AUTO_VERIFIED
// success panel — with the acceptance confirmation CPA-WEB12 added — never painted in the real app
// (reproduced against the real route tree; `router/claim-wizard-terminal.test.tsx` pins it). The same
// family as the invite door's remount (`features/admins/accept-invite-page.tsx`), cured the same way:
// the cache is what carries an answer across a remount, which a query has and component state does not.
//
// ★ CONSENT INTEGRITY. `acceptedVersion` is the label of the document the submit ECHOED — captured
// inside the mutation, from the same object whose version + hash rode the POST — never re-derived from
// the current Terms read. The remounted instance re-asks that read (its cache entry is stale by then),
// and a document rotated between the submit and the remount would otherwise put the NEW version's
// label on a confirmation for the OLD version's acceptance: a person told they accepted 1.2 when the
// §318(a) row pins 1.1. Pinned by rotating the served terms between submit and remount.
//
// ★ SHARED-DEVICE HYGIENE. The key carries the charity id and nothing else — like every charity query
// key here, none of which includes the uid — because Firebase identity is the app's hard cache
// boundary: `app.tsx` clears the whole query cache on ANY uid change (and remounts the route tree when
// leaving a signed-in user), and the portal's ONE sign-out (`auth/use-sign-out.ts`) clears it twice
// more. So a cached terminal result cannot outlive the session that produced it, and the next account
// on this device never sees another person's "You're verified" (pinned through the real `App`).
//
// ★ LIFETIME. `staleTime: Infinity` because a terminal result never goes stale by clock — it is a
// fact about a submit that happened; nothing may refetch it (there is no queryFn: `skipToken`). The
// observer's `gcTime` is left at the app default so the entry bridges the remount (milliseconds) and
// then lives only while a wizard for that charity is on screen plus the default grace — it is a bridge
// across a remount, not a record: the record is the backend's, and the console reads it from there.

import type { CharityClaimSubmitResultOut } from '@givmo/api-client';

/** What the wizard renders as its terminal panel, and what the submit's echo said it accepted. */
export interface ClaimTerminalResult {
  /** The submit's answer as the wire returned it. */
  result: CharityClaimSubmitResultOut;
  /**
   * The version label of the Charity Terms the submit ECHOED (`display_label`, else the ordinal —
   * `termsVersionLabel`), or `null` for a submit that echoed no acceptance.
   */
  acceptedVersion: string | null;
}

/**
 * The cache key: the CHARITY the submit was for, and nothing else. Hand-written, so no generated
 * query family matches it and no read ever refetches it. Not uid-scoped, by the app's rule — see
 * the header's hygiene note for why that is safe.
 */
export function claimTerminalResultKey(charityId: string): readonly unknown[] {
  return ['claim-wizard-terminal', charityId] as const;
}
